import {useEffect, useRef, useState} from 'react';
import {AwButton, AwCheckbox, AwDetailDrawer, AwField, AwSelect, AwTextArea} from '@ascension-tracker/ascension-ui';
import {api} from '../api.js';
import {useAsync} from '../hooks/useAsync.js';
import {useCreateTag, useProjectCustomFields, useProjectTags, useProjectTasks} from '../hooks/useTasks.js';
import {queryClient} from '../queryClient.js';
import {useDialogs} from './DialogProvider.js';

type TaskRecord = {id: string; title: string; notes: string; assignee_user_id: string | null; start_on: string | null; due_on: string | null; completed_at: string | null};
type TaskProjectLink = {projectId: string; projectKey: string; projectName: string; sectionId: string | null; sectionName: string | null};
type Subtask = {id: string; title: string; completedAt: string | null};
type Comment = {id: string; authorType: string; authorId: string; authorName: string; body: string; createdAt: string};
type Follower = {userId: string; displayName: string};
type Attachment = {id: string; originalName: string; mimeType: string; sizeBytes: number; createdAt: string};
type TaskTag = {id: string; name: string; color: string | null};
type CustomFieldValue = {customFieldId: string; fieldName: string; fieldType: string; valueText: string | null; valueNumber: number | null; valueDate: string | null; valueOptionId: string | null; valueOptionLabel: string | null; valueUserId: string | null; valueUserName: string | null};
type DependencyRef = {id: string; taskId: string; title: string; completedAt: string | null};
type TaskDetail = {task: TaskRecord; projects: TaskProjectLink[]; subtasks: Subtask[]; comments: Comment[]; followers: Follower[]; attachments: Attachment[]; tags: TaskTag[]; customFieldValues: CustomFieldValue[]; blockedBy: DependencyRef[]; blocking: DependencyRef[]};
type Member = {id: string; displayName: string; email: string};

function dateInputValue(value: string | null) { return value ? String(value).slice(0, 10) : ''; }
function formatWhen(value: string) { return new Date(value).toLocaleString(); }
function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function TaskDetailPanel({taskId, onClose}: {taskId: string | null; onClose: () => void}) {
  const {confirm} = useDialogs();
  const detail = useAsync(() => taskId ? api<TaskDetail>(`/tasks/${taskId}`) : Promise.resolve(null), [taskId]);
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [assigneeUserId, setAssigneeUserId] = useState('');
  const [startOn, setStartOn] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [subtaskTitle, setSubtaskTitle] = useState('');
  const [commentBody, setCommentBody] = useState('');
  const [followerId, setFollowerId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [dependsOnTaskId, setDependsOnTaskId] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastSavedRef = useRef({title: '', notes: '', assigneeUserId: '', startOn: '', dueOn: ''});
  const currentValuesRef = useRef({title: '', notes: '', assigneeUserId: '', startOn: '', dueOn: ''});
  currentValuesRef.current = {title, notes, assigneeUserId, startOn, dueOn};

  useEffect(() => {
    if (!detail.data) return;
    const loaded = {
      title: detail.data.task.title,
      notes: detail.data.task.notes,
      assigneeUserId: detail.data.task.assignee_user_id ?? '',
      startOn: dateInputValue(detail.data.task.start_on),
      dueOn: dateInputValue(detail.data.task.due_on)
    };
    setTitle(loaded.title);
    setNotes(loaded.notes);
    setAssigneeUserId(loaded.assigneeUserId);
    setStartOn(loaded.startOn);
    setDueOn(loaded.dueOn);
    lastSavedRef.current = loaded;
  }, [detail.data]);

  const projectId = detail.data?.projects[0]?.projectId;
  const members = useAsync(() => projectId ? api<{users: Member[]}>(`/projects/${projectId}/work-item-options?q=`) : Promise.resolve(null), [projectId]);
  const projectTags = useProjectTags(projectId ?? '');
  const customFields = useProjectCustomFields(projectId ?? '');
  const createTag = useCreateTag(projectId ?? '');
  const projectTasks = useProjectTasks(projectId ?? '');

  function refreshAfterListRelevantChange() {
    detail.reload();
    if (projectId) void queryClient.invalidateQueries({queryKey: ['tasks', projectId]});
  }

  async function flushSave() {
    if (!taskId) return;
    const current = currentValuesRef.current;
    const last = lastSavedRef.current;
    const changed = current.title !== last.title || current.notes !== last.notes || current.assigneeUserId !== last.assigneeUserId || current.startOn !== last.startOn || current.dueOn !== last.dueOn;
    if (!changed || !current.title.trim()) return;
    setSaving(true);
    try {
      await api(`/tasks/${taskId}`, {method: 'PATCH', body: JSON.stringify({title: current.title, notes: current.notes, assigneeUserId: current.assigneeUserId || null, startOn: current.startOn || null, dueOn: current.dueOn || null})});
      lastSavedRef.current = current;
      refreshAfterListRelevantChange();
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);
    } finally { setSaving(false); }
  }

  useEffect(() => {
    if (!taskId) return;
    const handle = setTimeout(() => { void flushSave(); }, 700);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, notes, assigneeUserId, startOn, dueOn, taskId]);

  function closeAndFlush() {
    void flushSave();
    onClose();
  }

  async function toggleComplete() {
    if (!taskId || !detail.data) return;
    await api(`/tasks/${taskId}/${detail.data.task.completed_at ? 'reopen' : 'complete'}`, {method: 'POST'});
    refreshAfterListRelevantChange();
  }
  async function addSubtask() {
    if (!taskId || !projectId || !subtaskTitle.trim()) return;
    await api('/tasks', {method: 'POST', body: JSON.stringify({projectId, parentTaskId: taskId, title: subtaskTitle.trim()})});
    setSubtaskTitle('');
    detail.reload();
  }
  async function addComment() {
    if (!taskId || !commentBody.trim()) return;
    await api(`/tasks/${taskId}/comments`, {method: 'POST', body: JSON.stringify({body: commentBody.trim()})});
    setCommentBody('');
    detail.reload();
  }
  async function addFollower() {
    if (!taskId || !followerId) return;
    await api(`/tasks/${taskId}/followers`, {method: 'POST', body: JSON.stringify({userId: followerId})});
    setFollowerId('');
    detail.reload();
  }
  async function removeFollower(userId: string) {
    if (!taskId) return;
    await api(`/tasks/${taskId}/followers/${userId}`, {method: 'DELETE'});
    detail.reload();
  }
  async function uploadFile(file: File) {
    if (!taskId) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api(`/tasks/${taskId}/attachments`, {method: 'POST', body: form});
      detail.reload();
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }
  async function removeAttachment(attachmentId: string) {
    await api(`/attachments/${attachmentId}`, {method: 'DELETE'});
    detail.reload();
  }
  async function attachExistingTag() {
    if (!taskId || !selectedTagId) return;
    await api(`/tasks/${taskId}/tags`, {method: 'POST', body: JSON.stringify({tagId: selectedTagId})});
    setSelectedTagId('');
    refreshAfterListRelevantChange();
  }
  async function createAndAttachTag() {
    if (!taskId || !newTagName.trim()) return;
    const tag = await createTag.mutateAsync({name: newTagName.trim()});
    setNewTagName('');
    await api(`/tasks/${taskId}/tags`, {method: 'POST', body: JSON.stringify({tagId: tag.id})});
    refreshAfterListRelevantChange();
  }
  async function detachTag(tagId: string) {
    if (!taskId) return;
    await api(`/tasks/${taskId}/tags/${tagId}`, {method: 'DELETE'});
    refreshAfterListRelevantChange();
  }
  async function setFieldValue(fieldId: string, value: {valueText?: string | null; valueNumber?: number | null; valueDate?: string | null; valueOptionId?: string | null; valueUserId?: string | null}) {
    if (!taskId) return;
    await api(`/tasks/${taskId}/custom-fields/${fieldId}`, {method: 'PATCH', body: JSON.stringify(value)});
    detail.reload();
  }
  async function addDependency() {
    if (!taskId || !dependsOnTaskId) return;
    await api(`/tasks/${taskId}/dependencies`, {method: 'POST', body: JSON.stringify({dependsOnTaskId})});
    setDependsOnTaskId('');
    detail.reload();
  }
  async function removeDependency(dependencyId: string) {
    if (!taskId) return;
    await api(`/tasks/${taskId}/dependencies/${dependencyId}`, {method: 'DELETE'});
    detail.reload();
  }
  async function deleteThisTask() {
    if (!taskId || !detail.data) return;
    const subtaskCount = detail.data.subtasks.length;
    const warning = subtaskCount > 0 ? `Delete "${detail.data.task.title}" and its ${subtaskCount} subtask${subtaskCount === 1 ? '' : 's'}? This cannot be undone.` : `Delete "${detail.data.task.title}"? This cannot be undone.`;
    const ok = await confirm(warning, {confirmLabel: 'Delete Task', danger: true});
    if (!ok) return;
    await api(`/tasks/${taskId}`, {method: 'DELETE'});
    if (projectId) void queryClient.invalidateQueries({queryKey: ['tasks', projectId]});
    void queryClient.invalidateQueries({queryKey: ['my-tasks']});
    onClose();
  }
  async function deleteSubtask(subtask: Subtask) {
    const ok = await confirm(`Delete subtask "${subtask.title}"?`, {confirmLabel: 'Delete Subtask', danger: true});
    if (!ok) return;
    await api(`/tasks/${subtask.id}`, {method: 'DELETE'});
    detail.reload();
  }

  const availableFollowerOptions = (members.data?.users ?? []).filter(user => !detail.data?.followers.some(follower => follower.userId === user.id));
  const availableTagOptions = (projectTags.data ?? []).filter(tag => !detail.data?.tags.some(taskTag => taskTag.id === tag.id));
  const availableDependencyOptions = (projectTasks.data ?? []).filter(candidate =>
    candidate.id !== taskId &&
    !detail.data?.blockedBy.some(dependency => dependency.taskId === candidate.id) &&
    !detail.data?.blocking.some(dependency => dependency.taskId === candidate.id)
  );

  return (
    <AwDetailDrawer open={taskId !== null} title={detail.data?.task.title ?? 'Task'} onClose={closeAndFlush}>
      {!detail.data ? <p>Loading…</p> : (
        <div className="task-detail">
          <div className="task-detail__save-status" aria-live="polite">{saving ? 'Saving…' : justSaved ? 'Saved' : ''}</div>
          <AwCheckbox id="task-detail-complete" label={detail.data.task.completed_at ? 'Completed' : 'Mark complete'} checked={Boolean(detail.data.task.completed_at)} onChange={toggleComplete}/>
          <AwField id="task-detail-title" label="Title" value={title} onChange={event => setTitle(event.target.value)}/>
          <AwSelect id="task-detail-assignee" label="Assignee" value={assigneeUserId} onChange={event => setAssigneeUserId(event.target.value)}>
            <option value="">Unassigned</option>
            {members.data?.users.map(user => <option key={user.id} value={user.id}>{user.displayName}</option>)}
          </AwSelect>
          <div className="task-detail__dates">
            <AwField id="task-detail-start" label="Start date" type="date" value={startOn} onChange={event => setStartOn(event.target.value)}/>
            <AwField id="task-detail-due" label="Due date" type="date" value={dueOn} onChange={event => setDueOn(event.target.value)}/>
          </div>
          <AwTextArea id="task-detail-notes" label="Notes" rows={5} value={notes} onChange={event => setNotes(event.target.value)}/>

          <section className="task-detail__tags">
            <h3>Tags</h3>
            <div className="tag-manager">
              {detail.data.tags.map(tag => (
                <span key={tag.id} className="tag-chip" style={tag.color ? {borderColor: tag.color, color: tag.color} : undefined}>
                  {tag.name}
                  <button type="button" aria-label={`Remove tag ${tag.name}`} onClick={() => detachTag(tag.id)}>×</button>
                </span>
              ))}
            </div>
            {availableTagOptions.length > 0 && (
              <div className="task-quick-add">
                <AwSelect id="task-detail-add-tag" label="" value={selectedTagId} onChange={event => setSelectedTagId(event.target.value)}>
                  <option value="">Add a tag…</option>
                  {availableTagOptions.map(tag => <option key={tag.id} value={tag.id}>{tag.name}</option>)}
                </AwSelect>
                <AwButton type="button" variant="secondary" onClick={attachExistingTag} disabled={!selectedTagId}>Add</AwButton>
              </div>
            )}
            <div className="task-quick-add">
              <AwField id="task-detail-new-tag" label="" placeholder="Create new tag…" value={newTagName} onChange={event => setNewTagName(event.target.value)}/>
              <AwButton type="button" variant="secondary" onClick={createAndAttachTag} disabled={!newTagName.trim()}>Create</AwButton>
            </div>
          </section>

          {(customFields.data ?? []).length > 0 && (
            <section className="task-detail__fields">
              <h3>Fields</h3>
              {customFields.data!.map(field => {
                const current = detail.data!.customFieldValues.find(value => value.customFieldId === field.id);
                if (field.fieldType === 'single_select') return (
                  <AwSelect key={field.id} id={`task-detail-field-${field.id}`} label={field.name} value={current?.valueOptionId ?? ''} onChange={event => setFieldValue(field.id, {valueOptionId: event.target.value || null})}>
                    <option value="">—</option>
                    {field.options.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
                  </AwSelect>
                );
                if (field.fieldType === 'people') return (
                  <AwSelect key={field.id} id={`task-detail-field-${field.id}`} label={field.name} value={current?.valueUserId ?? ''} onChange={event => setFieldValue(field.id, {valueUserId: event.target.value || null})}>
                    <option value="">—</option>
                    {members.data?.users.map(user => <option key={user.id} value={user.id}>{user.displayName}</option>)}
                  </AwSelect>
                );
                if (field.fieldType === 'number') return (
                  <AwField key={field.id} id={`task-detail-field-${field.id}`} label={field.name} type="number" defaultValue={current?.valueNumber ?? ''} onBlur={event => setFieldValue(field.id, {valueNumber: event.target.value === '' ? null : Number(event.target.value)})}/>
                );
                if (field.fieldType === 'date') return (
                  <AwField key={field.id} id={`task-detail-field-${field.id}`} label={field.name} type="date" defaultValue={dateInputValue(current?.valueDate ?? null)} onBlur={event => setFieldValue(field.id, {valueDate: event.target.value || null})}/>
                );
                return (
                  <AwField key={field.id} id={`task-detail-field-${field.id}`} label={field.name} defaultValue={current?.valueText ?? ''} onBlur={event => setFieldValue(field.id, {valueText: event.target.value || null})}/>
                );
              })}
            </section>
          )}

          <section className="task-detail__dependencies">
            <h3>Dependencies</h3>
            <p className="task-detail__label">Blocked by</p>
            {detail.data.blockedBy.length === 0 && <p className="task-detail__muted">Not blocked by anything.</p>}
            {detail.data.blockedBy.map(dependency => (
              <div key={dependency.id} className="task-detail__chip">
                <span>{dependency.completedAt ? '✓' : '○'} {dependency.title}</span>
                <button type="button" aria-label={`Remove dependency on ${dependency.title}`} onClick={() => removeDependency(dependency.id)}>×</button>
              </div>
            ))}
            {availableDependencyOptions.length > 0 && (
              <div className="task-quick-add">
                <AwSelect id="task-detail-add-dependency" label="" value={dependsOnTaskId} onChange={event => setDependsOnTaskId(event.target.value)}>
                  <option value="">Add blocking task…</option>
                  {availableDependencyOptions.map(candidate => <option key={candidate.id} value={candidate.id}>{candidate.title}</option>)}
                </AwSelect>
                <AwButton type="button" variant="secondary" onClick={addDependency} disabled={!dependsOnTaskId}>Add</AwButton>
              </div>
            )}
            {detail.data.blocking.length > 0 && (
              <>
                <p className="task-detail__label">Blocking</p>
                {detail.data.blocking.map(dependency => (
                  <div key={dependency.id} className="task-detail__chip">
                    <span>{dependency.completedAt ? '✓' : '○'} {dependency.title}</span>
                  </div>
                ))}
              </>
            )}
          </section>

          <section className="task-detail__subtasks">
            <h3>Subtasks</h3>
            {detail.data.subtasks.map(subtask => (
              <div key={subtask.id} className="task-detail__chip">
                <span>{subtask.completedAt ? '✓' : '○'} {subtask.title}</span>
                <button type="button" aria-label={`Delete subtask ${subtask.title}`} onClick={() => deleteSubtask(subtask)}>×</button>
              </div>
            ))}
            <div className="task-quick-add">
              <AwField id="task-detail-new-subtask" label="" placeholder="Add subtask…" value={subtaskTitle} onChange={event => setSubtaskTitle(event.target.value)}/>
              <AwButton type="button" variant="secondary" onClick={addSubtask} disabled={!subtaskTitle.trim()}>Add</AwButton>
            </div>
          </section>

          <section className="task-detail__followers">
            <h3>Followers</h3>
            {detail.data.followers.length === 0 && <p className="task-detail__muted">No followers yet.</p>}
            {detail.data.followers.map(follower => (
              <div key={follower.userId} className="task-detail__chip">
                <span>{follower.displayName}</span>
                <button type="button" aria-label={`Remove ${follower.displayName}`} onClick={() => removeFollower(follower.userId)}>×</button>
              </div>
            ))}
            {availableFollowerOptions.length > 0 && (
              <div className="task-quick-add">
                <AwSelect id="task-detail-add-follower" label="" value={followerId} onChange={event => setFollowerId(event.target.value)}>
                  <option value="">Add a follower…</option>
                  {availableFollowerOptions.map(user => <option key={user.id} value={user.id}>{user.displayName}</option>)}
                </AwSelect>
                <AwButton type="button" variant="secondary" onClick={addFollower} disabled={!followerId}>Add</AwButton>
              </div>
            )}
          </section>

          <section className="task-detail__attachments">
            <h3>Attachments</h3>
            {detail.data.attachments.length === 0 && <p className="task-detail__muted">No attachments yet.</p>}
            {detail.data.attachments.map(attachment => (
              <div key={attachment.id} className="task-detail__attachment">
                <a href={`/api/v1/attachments/${attachment.id}/download`}>{attachment.originalName}</a>
                <span>{formatSize(attachment.sizeBytes)}</span>
                <button type="button" aria-label={`Remove ${attachment.originalName}`} onClick={() => removeAttachment(attachment.id)}>×</button>
              </div>
            ))}
            <input ref={fileInputRef} type="file" onChange={event => { const file = event.target.files?.[0]; if (file) void uploadFile(file); }} disabled={uploading}/>
          </section>

          <section className="task-detail__comments">
            <h3>Comments</h3>
            {detail.data.comments.map(comment => (
              <div key={comment.id} className="task-detail__comment">
                <strong>{comment.authorName}</strong>
                <span>{formatWhen(comment.createdAt)}</span>
                <p>{comment.body}</p>
              </div>
            ))}
            <AwTextArea id="task-detail-new-comment" label="" placeholder="Leave a comment…" rows={3} value={commentBody} onChange={event => setCommentBody(event.target.value)}/>
            <AwButton type="button" variant="secondary" onClick={addComment} disabled={!commentBody.trim()}>Comment</AwButton>
          </section>

          <section className="task-detail__danger-zone">
            <AwButton type="button" variant="danger" onClick={deleteThisTask}>Delete Task</AwButton>
          </section>
        </div>
      )}
    </AwDetailDrawer>
  );
}
